<script setup lang="ts">
import { DEFAULT_SECTION_TITLES, type TextSettings, type ThemeSettings, type SectionSettings } from '~/stores/auth'
import type { SetupItem } from '~/stores/setups'
import { getCategoryLabel, getCategoryIcon } from '~/stores/setups'

const props = defineProps<{
  setupItems: SetupItem[]
  setupPhotoUrl: string | null
  textSettings: TextSettings
  themeSettings: ThemeSettings
  sectionSettings: SectionSettings
  characterId?: string
  editMode?: boolean
}>()

const emit = defineEmits<{
  'open-setup-manager': []
}>()

const hasContent = computed(() => props.setupItems.length > 0 || !!props.setupPhotoUrl)
const photoOnly = computed(() => props.setupItems.length === 0 && !!props.setupPhotoUrl)

const showFullPhoto = ref(false)

// Group items by category, preserving order
const groupedItems = computed(() => {
  const groups: { category: string, label: string, icon: string, items: SetupItem[] }[] = []
  const map = new Map<string, SetupItem[]>()

  for (const item of props.setupItems) {
    if (!map.has(item.category)) {
      map.set(item.category, [])
    }
    map.get(item.category)!.push(item)
  }

  for (const [category, items] of map) {
    groups.push({
      category,
      label: getCategoryLabel(category),
      icon: getCategoryIcon(category),
      items
    })
  }

  return groups
})

// Heading and divider styles
const injectedResolvedFont = inject<ComputedRef<string>>('resolvedFontFamily', computed(() => ''))
const headingStyle = computed(() => ({
  fontSize: `calc(1rem * ${props.textSettings.fontScale})`,
  fontWeight: props.textSettings.fontWeight,
  fontFamily: `'${injectedResolvedFont.value || props.textSettings.fontFamily}', sans-serif`,
  color: `color-mix(in srgb, ${props.textSettings.color} 70%, transparent)`,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.1em'
}))

const itemTextStyle = computed(() => ({
  fontFamily: `'${injectedResolvedFont.value || props.textSettings.fontFamily}', sans-serif`,
  color: props.textSettings.color
}))

const injectedSeparatorStyle = inject<Ref<Record<string, string> | null>>('separatorStyle', ref({ background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)' }))
const dividerStyle = computed(() => injectedSeparatorStyle.value ?? { background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)' })

const inCard = inject<Ref<boolean>>('sectionInCard', ref(false))
const showTitles = computed(() => props.sectionSettings.showTitles !== false)

// Custom section titles
const authStore = useAuthStore()
const { requirePlan } = usePlanGate()
const sectionTitlesEnabled = computed(() => authStore.canAccessBoolean('customSectionTitles'))
const sectionTitle = computed(() => props.sectionSettings.titles?.setup || DEFAULT_SECTION_TITLES.setup)
const updateSectionTitle = inject<(key: string, value: string) => void>('updateSectionTitle', () => {})

function trackSetupClick(item: SetupItem) {
  if (props.characterId && !props.editMode) {
    $fetch(`/api/character/${props.characterId}/setup/${item.id}/click`, {
      method: 'POST',
      body: { sessionId: useStatisticsSession() }
    }).catch(() => {})
  }
}

function getLinkDomain(url: string): string {
  try {
    return new URL(url).hostname.replace('www.', '')
  } catch {
    return url
  }
}
</script>

<template>
  <div
    v-if="hasContent || editMode"
    class="px-2"
    :class="{ 'mt-6': showTitles || !inCard }"
  >
    <!-- Section heading with divider -->
    <div
      v-if="showTitles || !inCard"
      class="flex items-center gap-3 mb-4"
    >
      <div
        class="flex-1 h-px"
        :style="dividerStyle"
      />
      <!-- Edit mode + Pro+ → click to edit -->
      <InlineEditText
        v-if="showTitles && editMode && sectionTitlesEnabled"
        :model-value="sectionSettings.titles?.setup ?? ''"
        :editable="true"
        tag="h3"
        :placeholder="DEFAULT_SECTION_TITLES.setup"
        :display-style="headingStyle"
        class="shrink-0"
        @update:model-value="updateSectionTitle('setup', $event)"
      />
      <!-- Edit mode + Free → locked with Pro badge -->
      <div
        v-else-if="showTitles && editMode"
        class="shrink-0 inline-flex items-center gap-1.5 cursor-pointer rounded-lg transition-colors hover:bg-white/5 px-2 py-0.5"
        @click="requirePlan('Custom Section Titles', 'pro')"
      >
        <h3
          class="text-xs"
          :style="headingStyle"
        >
          {{ DEFAULT_SECTION_TITLES.setup }}
        </h3>
        <UBadge
          color="fuchsia"
          variant="solid"
          size="xs"
        >
          <UIcon
            name="i-lucide-pencil"
            class="size-2.5"
          />
        </UBadge>
      </div>
      <!-- Public view → static text -->
      <h3
        v-else-if="showTitles"
        class="text-xs shrink-0"
        :style="headingStyle"
      >
        {{ sectionTitle }}
      </h3>
      <div
        v-if="showTitles"
        class="flex-1 h-px"
        :style="dividerStyle"
      />
    </div>

    <!-- Photo only (no items) - full width -->
    <button
      v-if="photoOnly && setupPhotoUrl"
      class="group/thumb relative w-full rounded-lg overflow-hidden cursor-pointer"
      @click="showFullPhoto = true"
    >
      <img
        :src="setupPhotoUrl"
        alt="My Setup"
        class="w-full max-h-48 object-contain rounded-lg"
      >
      <div class="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/thumb:bg-black/40 transition-all duration-200 rounded-lg">
        <UIcon
          name="i-lucide-zoom-in"
          class="size-5 text-white/80 opacity-0 group-hover/thumb:opacity-100 transition-opacity duration-200"
        />
      </div>
    </button>

    <!-- Content: items + photo thumbnail -->
    <div
      v-else-if="groupedItems.length > 0"
      class="flex gap-4"
    >
      <!-- Grouped items (takes remaining space) -->
      <div class="flex-1 min-w-0 space-y-3">
        <div
          v-for="group in groupedItems"
          :key="group.category"
        >
          <div class="flex items-center gap-1.5 mb-1.5">
            <UIcon
              :name="group.icon"
              class="size-3 opacity-40"
              :style="itemTextStyle"
            />
            <span
              class="text-[10px] uppercase tracking-wider opacity-40"
              :style="itemTextStyle"
            >{{ group.label }}</span>
          </div>
          <div class="space-y-1">
            <div
              v-for="item in group.items"
              :key="item.id"
              class="flex items-baseline gap-2"
            >
              <UTooltip
                v-if="item.link"
                :text="`Opens ${getLinkDomain(item.link)}`"
              >
                <NuxtLink
                  :to="item.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="group/link inline-flex items-baseline gap-1 text-sm opacity-80 hover:opacity-100 underline decoration-dotted underline-offset-4 decoration-current/30 hover:decoration-current/60 transition-all"
                  :style="itemTextStyle"
                  @click="trackSetupClick(item)"
                >
                  <span>{{ item.name }}</span>
                  <UIcon
                    name="i-lucide-external-link"
                    class="size-3 opacity-40 group-hover/link:opacity-70 transition-opacity self-center shrink-0"
                  />
                </NuxtLink>
              </UTooltip>
              <span
                v-else
                class="text-sm opacity-80"
                :style="itemTextStyle"
              >{{ item.name }}</span>
              <span
                v-if="item.value"
                class="text-xs opacity-40"
                :style="itemTextStyle"
              >{{ item.value }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Photo thumbnail (top-right, responsive) -->
      <button
        v-if="setupPhotoUrl"
        class="group/thumb relative shrink-0 w-1/4 min-w-20 max-w-40 rounded-lg overflow-hidden cursor-pointer self-start"
        @click="showFullPhoto = true"
      >
        <img
          :src="setupPhotoUrl"
          alt="My Setup"
          class="w-full object-contain rounded-lg"
        >
        <div class="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/thumb:bg-black/40 transition-all duration-200 rounded-lg">
          <UIcon
            name="i-lucide-zoom-in"
            class="size-5 text-white/80 opacity-0 group-hover/thumb:opacity-100 transition-opacity duration-200"
          />
        </div>
      </button>
    </div>

    <!-- Full photo overlay -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-200"
        leave-active-class="transition-opacity duration-200"
        enter-from-class="opacity-0"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showFullPhoto && setupPhotoUrl"
          class="fixed inset-0 z-100 flex items-center justify-center bg-black/80 backdrop-blur-sm cursor-pointer p-4"
          @click="showFullPhoto = false"
        >
          <img
            :src="setupPhotoUrl"
            alt="My Setup"
            class="max-w-full max-h-[90vh] object-contain rounded-lg"
            @click.stop
          >
          <button
            class="absolute top-4 right-4 flex items-center justify-center size-10 rounded-full bg-black/60 border border-white/10 hover:bg-black/80 transition-colors cursor-pointer"
            @click="showFullPhoto = false"
          >
            <UIcon
              name="i-lucide-x"
              class="size-5 text-white/70"
            />
          </button>
        </div>
      </Transition>
    </Teleport>

    <!-- Edit mode: manage button -->
    <button
      v-if="editMode"
      class="mt-3 w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-dashed transition-all duration-200 hover:scale-105 hover:border-white/15"
      :style="{
        borderColor: 'rgba(255, 255, 255, 0.08)',
        color: 'rgba(255, 255, 255, 0.4)'
      }"
      @click="emit('open-setup-manager')"
    >
      <UIcon
        name="i-lucide-settings"
        class="size-4"
      />
      <span class="text-sm">Manage setup</span>
    </button>
  </div>
</template>
